// The `activity` route (`/transactions`, design `#s05` feed). One feed
// grouped by day over every account; the shell's global account filter
// narrows it (`useShellContext().accountFilter`). Cursor-based infinite
// loading via `useTransactions` (`useInfiniteQuery`). A bottom sentinel
// fetches the next page when it intersects; there is no "Load more" button
// and no "More transactions are available" caption. The "Likely transfer?"
// review card sits in the feed at the date of its later leg.
import { useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@moneyskyline/ui';
import { useShellContext } from '@/app/shell/AppShell';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { HonestInline } from '@/components/HonestInline';
import { useTransactions } from '@/api/hooks/useTransactions';
import { useCategories } from '@/api/hooks/useCategories';
import {
  useBulkLineItemPatch,
  useBulkLineItemPreview,
  type AnnotationEdit,
  type BulkPreviewResponse,
  type MutationResult,
  type AnnotationMutationData,
} from '@/api/hooks/useLineItemPatch';
import { useBatchUndo, type AuditBatchUndoData } from '@/api/hooks/useBatchUndo';
import { AccountScopeUnavailableError } from '@/api/hooks/accountScope';
import { useLinkCandidates } from '@/api/hooks/useLinkCandidates';
import { useConfirmLink } from '@/api/hooks/useConfirmLink';
import { CategorizationTools } from './tools/CategorizationTools';
import { JobStatusRow } from './tools/JobStatusRow';
import { FeedRow } from './FeedRow';
import { FeedNextPage } from './FeedNextPage';
import { TransferReviewCard } from './TransferReviewCard';
import { ViewSwitcher } from './ViewSwitcher';
import { titleCaseFromSlug } from './spendAggregation';
import type {
  CanonicalTransactionRow,
  PrimaryTransactionsMeta,
  ParsedPrimaryTransactionsPage,
  TransactionRow,
} from '@/api/hooks/useTransactions';
import type { LinkCandidate } from '@/api/hooks/useLinkCandidates';
import { getScopeDateBounds } from './transactionDateRange';
import { scopeHref } from '@/app/shell/scope';
import { DateScopeGuard } from '@/app/shell/DateScopeGuard';
import { candidateInTransactionScope, matchesCategoryFilter, matchesCategoryIdFilter, matchesMerchantFilter, matchesMerchantIdFilter } from './transactionDrillIn';
import { classifyReviewLaunch, type ReviewLaunchEligibility } from '@/features/reconciliation/reviewLaunch';
import { ReconciliationReviewPanel, type ReconciliationLaunch } from '@/features/reconciliation/ReconciliationReviewPanel';

type CanonicalFeedPage = Extract<ParsedPrimaryTransactionsPage, { kind: 'canonical' }>;
type CanonicalFeedMeta = CanonicalFeedPage['meta'];
type TransactionsQueryPage = {
  data: Array<CanonicalTransactionRow | TransactionRow>;
  meta: PrimaryTransactionsMeta;
};

type SelectedLineItem = {
  expectedVersion: number;
  canonicalTransactionId: string;
};

type BulkPhase = 'idle' | 'previewing' | 'previewed' | 'committing' | 'undoing' | 'error' | 'saved';

type PublicMutationResult = MutationResult<AnnotationMutationData>;
type PublicUndoResult = MutationResult<AuditBatchUndoData>;

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function patchReturnedLineItems(value: unknown, returned: Map<string, AnnotationMutationData['line_items'][number]>): { value: unknown; changed: boolean } {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const patched = patchReturnedLineItems(entry, returned);
      changed ||= patched.changed;
      return patched.value;
    });
    return { value: changed ? next : value, changed };
  }
  if (typeof value !== 'object' || value === null) return { value, changed: false };

  const candidate = value as { line_item_id?: unknown };
  const replacement = typeof candidate.line_item_id === 'string' ? returned.get(candidate.line_item_id) : undefined;
  let next: Record<string, unknown> | object = replacement ? { ...(value as object), ...replacement } : value;
  let changed = Boolean(replacement);
  for (const [key, child] of Object.entries(next)) {
    const patched = patchReturnedLineItems(child, returned);
    if (patched.changed) {
      if (next === value) next = { ...(value as object) };
      (next as Record<string, unknown>)[key] = patched.value;
      changed = true;
    }
  }
  return { value: next, changed };
}

function patchLineItemCaches(queryClient: ReturnType<typeof useQueryClient>, lineItems: AnnotationMutationData['line_items']): Set<string> {
  const returned = new Map(lineItems.map((lineItem) => [lineItem.line_item_id, lineItem]));
  const patchedRoots = new Set<string>();
  for (const query of queryClient.getQueryCache().findAll()) {
    const root = query.queryKey[0];
    if (typeof root !== 'string' || !['transactions', 'inbox', 'spend-by-category', 'spend-by-merchant'].includes(root)) continue;
    queryClient.setQueryData(query.queryKey, (current: unknown) => {
      const patched = patchReturnedLineItems(current, returned);
      if (patched.changed) patchedRoots.add(root);
      return patched.value;
    });
  }
  return patchedRoots;
}

function SelectableFeedDayGroup({
  label,
  rows,
  selected,
  onToggle,
}: {
  label: string;
  rows: CanonicalTransactionRow[];
  selected: Map<string, SelectedLineItem>;
  onToggle: (row: CanonicalTransactionRow, checked: boolean) => void;
}) {
  return (
    <section data-testid="feed-day-group" className="mb-3">
      <div className="mb-1.5 text-xs text-muted-foreground">{label}</div>
      <div className="rounded-lg border border-border bg-card" data-testid="feed-day-rows">
        {rows.map((row) => {
          const lineItem = row.line_items[0];
          if (!lineItem) return <FeedRow key={row.transaction_id} txn={row} />;
          const merchant = row.merchant?.display_name ?? row.merchant_raw;
          return (
            <div key={row.transaction_id} className="grid grid-cols-[44px_minmax(0,1fr)] items-stretch" data-testid="bulk-select-row" data-line-item-id={lineItem.line_item_id}>
              <label className="ms-tap-44 inline-flex min-h-11 min-w-11 items-center justify-center" aria-label={`Select ${merchant}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded-sm border border-input accent-primary"
                  aria-label={`Select ${merchant}`}
                  checked={selected.has(lineItem.line_item_id)}
                  onChange={(event) => onToggle(row, event.target.checked)}
                />
              </label>
              <FeedRow txn={row} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function isAccountScopeUnavailableError(error: unknown): boolean {
  if (error instanceof AccountScopeUnavailableError) return true;
  return typeof error === 'object' && error !== null && 'reason' in error && error.reason === 'multi_account_scope_unavailable';
}

function isParsedPrimaryPage(page: unknown): page is ParsedPrimaryTransactionsPage | TransactionsQueryPage {
  if (typeof page !== 'object' || page === null) return false;
  if ('kind' in page && (page.kind === 'canonical' || page.kind === 'initialization_required')) return true;
  if (!('data' in page) || !Array.isArray(page.data) || !('meta' in page) || typeof page.meta !== 'object' || page.meta === null) return false;
  const initialization = 'reconciliation_initialization_required' in page.meta && page.meta.reconciliation_initialization_required === true;
  if (!initialization && !('completeness' in page.meta)) return false;
  const canonicalRows = page.data.length === 0 || page.data.every((row) =>
    typeof row === 'object' && row !== null && 'canonical_transaction_id' in row,
  );
  return initialization || canonicalRows;
}

function normalizePrimaryPage(page: ParsedPrimaryTransactionsPage | TransactionsQueryPage): ParsedPrimaryTransactionsPage {
  if ('kind' in page) return page;
  if (page.meta.reconciliation_initialization_required === true) {
    return { kind: 'initialization_required', data: page.data, meta: { ...page.meta, reconciliation_initialization_required: true } };
  }
  return { kind: 'canonical', data: page.data as CanonicalTransactionRow[], meta: page.meta };
}

function dayKey(isoDate: string): string {
  return isoDate.slice(0, 10);
}

function dayLabel(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function postedDayLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function groupByDay(rows: CanonicalTransactionRow[], usePostedDay: boolean): { key: string; label: string; rows: CanonicalTransactionRow[] }[] {
  const groups = new Map<string, CanonicalTransactionRow[]>();
  for (const row of rows) {
    const key = usePostedDay ? row.posted_day : dayKey(row.posted_at);
    if (typeof key !== 'string') {
      throw new Error('confirmed transaction rows require a server-authoritative posted_day');
    }
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, groupRows]) => ({
      key,
      label: usePostedDay ? postedDayLabel(key) : dayLabel(groupRows[0].posted_at),
      rows: groupRows,
    }));
}

/** Groups unconfirmed candidates by the day of their LATER leg. */
function candidatesByDay(candidates: LinkCandidate[]): Map<string, LinkCandidate[]> {
  const map = new Map<string, LinkCandidate[]>();
  for (const candidate of candidates) {
    const laterLeg = candidate.legs[0].posted_at > candidate.legs[1].posted_at ? candidate.legs[0] : candidate.legs[1];
    const key = dayKey(laterLeg.posted_at);
    const bucket = map.get(key);
    if (bucket) bucket.push(candidate);
    else map.set(key, [candidate]);
  }
  return map;
}

function historyNotice(meta: CanonicalFeedMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.completeness.history_status === 'failed') return 'Transaction history could not be fully loaded. Some history may be missing.';
  if (meta.completeness.history_status === 'loading') return 'Transaction history is still loading.';
  if (meta.completeness.completeness === 'incomplete') return 'Transaction history is incomplete. More history may still be loading.';
  return null;
}

function AccountReviewHost({
  launch,
  location,
  onViewProgress,
}: {
  launch: ReconciliationLaunch;
  location: { search: string; hash: string };
  onViewProgress: () => void;
}) {
  const returnFocusRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const launchKey = `${launch.canonicalAccountId}:${launch.replacementItemId}`;
  return (
    <div className="w-full" data-testid="reconciliation-review-host">
      {/* ENG-F-001: the review-eligible-account header is a SEPARATE branch
          from TransactionsPage's normal header, but it must render the same
          single shared "Categorization tools" entry -- Reclassify and "Tell
          me a rule" must not disappear on an account that also has a
          reconciliation review pending. */}
      <header className="flex items-center justify-between gap-3">
        <h1 id="activity-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            ref={returnFocusRef}
            type="button"
            className="min-h-11 inline-flex items-center text-sm text-primary underline"
            onClick={() => setOpen(true)}
          >
            Review account changes
          </button>
          <CategorizationTools onViewProgress={onViewProgress} />
          <Link to={scopeHref(`/transactions/inbox${location.hash}`, location.search)} className="min-h-11 inline-flex items-center text-sm text-primary underline">Review</Link>
        </div>
      </header>
      <div className="mt-3 w-full" data-testid="reconciliation-review-panel-host">
        <ReconciliationReviewPanel
          key={launchKey}
          launch={launch}
          open={open}
          onOpenChange={setOpen}
          returnFocusRef={returnFocusRef}
        />
      </div>
    </div>
  );
}

function TransactionsPageContent() {
  const {
    accountFilter,
    accountLabel,
    accountIds,
    accountScopeAvailability,
    dateFrom: scopeFrom,
    dateTo: scopeTo,
    scopeAvailability,
    retryScopeAvailability,
    canonicalAccounts,
  } = useShellContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const merchantFilter = searchParams.get('merchant');
  const categoryIdFilter = searchParams.get('category_id');
  const merchantIdFilter = searchParams.get('merchant_id');
  const { from: dateFrom, to: dateTo } = getScopeDateBounds({ dateFrom: scopeFrom, dateTo: scopeTo });
  const accountScopeReady = accountScopeAvailability === undefined || accountScopeAvailability === 'ready';
  const canonicalReady = scopeAvailability === 'ready' && canonicalAccounts !== undefined && accountScopeReady;
  const transactions = useTransactions({ accountIds, dateFrom, dateTo, enabled: canonicalReady });
  const accountScopeError = isAccountScopeUnavailableError(transactions.error);
  const temporalUnavailable = transactions.temporal?.availability === 'unavailable'
    && transactions.temporal.reason !== 'awaiting_response'
    && transactions.temporal.reason !== 'metadata_absent';
  const candidatesQuery = useLinkCandidates(canonicalReady && !accountScopeError && !temporalUnavailable);
  const confirmLink = useConfirmLink();
  const queryClient = useQueryClient();
  const categoriesQuery = useCategories();
  const bulkPreview = useBulkLineItemPreview();
  const bulkPatch = useBulkLineItemPatch();
  const batchUndo = useBatchUndo();
  const [pendingCandidateIds, setPendingCandidateIds] = useState<Set<string>>(() => new Set());
  const [candidateErrors, setCandidateErrors] = useState<Record<string, string>>({});
  const [selectedLineItems, setSelectedLineItems] = useState<Map<string, SelectedLineItem>>(() => new Map());
  const [bulkPhase, setBulkPhase] = useState<BulkPhase>('idle');
  const [bulkPreviewData, setBulkPreviewData] = useState<BulkPreviewResponse | null>(null);
  const [bulkEdits, setBulkEdits] = useState<AnnotationEdit[]>([]);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);
  const [lastBatchCount, setLastBatchCount] = useState(0);
  const [lastBatchUndone, setLastBatchUndone] = useState(false);

  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isOffline) return <RouteRecoveryScreen kind="offline" frame="inline" />;

  if (accountScopeAvailability === 'unavailable' || accountScopeError) {
    return (
      <div className="mt-4 space-y-2">
        <HonestInline kind="api-unavailable" />
        <p className="text-sm text-muted-foreground" data-testid="account-scope-unavailable-copy">
          Multiple selected accounts are not available yet. Choose one account or All accounts to view transactions.
        </p>
      </div>
    );
  }

  if (scopeAvailability !== 'ready') {
    return (
      <div className="mx-auto w-full max-w-5xl p-4">
        {scopeAvailability === 'pending' ? (
          <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">Checking transaction scope availability…</p>
        ) : (
          <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">
            Transaction scope availability could not be checked.{' '}
            <button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>Retry</button>
          </p>
        )}
      </div>
    );
  }

  if (!canonicalReady) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable until canonical data is ready.</p>;
  }

  if (temporalUnavailable) {
    return (
      <div className="mt-4 space-y-2">
        <HonestInline kind="api-unavailable" />
        <p className="text-sm text-muted-foreground" data-testid="temporal-scope-unavailable-copy">
          Transaction history is unavailable because its saved-timezone scope could not be confirmed.
        </p>
      </div>
    );
  }

  if (transactions.isPending) {
    return (
      <div className="mt-4 space-y-2" data-testid="feed-loading-skeleton">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  if (transactions.isError) return <RouteRecoveryScreen kind="api-unavailable" frame="inline" />;

  const pages = transactions.data?.pages ?? [];
  if (!transactions.data || pages.length === 0) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable because no canonical history page was returned.</p>;
  }
  const parsedPages = pages.filter(isParsedPrimaryPage).map(normalizePrimaryPage);
  if (parsedPages.length !== pages.length) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable because no canonical history page was returned.</p>;
  }
  const initializationPage = parsedPages.find((page) => page.kind === 'initialization_required');
  if (initializationPage) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history needs setup. Feed is unavailable.</p>;
  }

  const canonicalPages = parsedPages.filter((page): page is CanonicalFeedPage => page.kind === 'canonical');
  const allRows = canonicalPages.flatMap((page) => page.data);
  const lineItemRows = new Map(allRows.flatMap((row) => row.line_items.length > 0 ? [[row.line_items[0].line_item_id, row] as const] : []));
  const rows = allRows.filter((row) =>
    (!categoryFilter || matchesCategoryFilter(row, categoryFilter)) &&
    (!categoryIdFilter || matchesCategoryIdFilter(row, categoryIdFilter)) &&
    (!merchantFilter || matchesMerchantFilter(row, merchantFilter)) &&
    (!merchantIdFilter || matchesMerchantIdFilter(row, merchantIdFilter)),
  );
  const lastMeta = canonicalPages[canonicalPages.length - 1]?.meta;
  const notice = historyNotice(lastMeta);
  const temporalMetadataAbsent = transactions.temporal?.availability === 'unavailable'
    && transactions.temporal.reason === 'metadata_absent';
  const candidates = (candidatesQuery.data ?? []).filter((candidate) => candidateInTransactionScope(candidate, {
    accountId: accountIds?.length === 1 ? accountIds[0] : undefined,
    dateFrom,
    dateTo,
  }));
  const candidateMap = candidatesByDay(candidates);
  const groups = groupByDay(rows, transactions.temporal?.availability === 'confirmed');
  const groupByKey = new Map(groups.map((group) => [group.key, group]));
  const dayKeys = [...new Set([...groups.map((group) => group.key), ...candidateMap.keys()])]
    .sort((a, b) => (a < b ? 1 : -1));
  const merchantLabel = merchantIdFilter
    ? allRows.find((row) => row.merchant?.merchant_id === merchantIdFilter)?.merchant?.display_name ?? 'Selected merchant'
    : merchantFilter
      ? merchantFilter.startsWith('raw:')
        ? merchantFilter.slice(4) || 'Selected merchant'
        : allRows.find((row) => row.merchant?.merchant_id === merchantFilter)?.merchant?.display_name ?? 'Selected merchant'
      : null;
  const categoryFilterLabel = categoryIdFilter
    ? categoriesQuery.data?.categories.find((category) => category.category_id === categoryIdFilter)?.name
      ?? 'Selected category'
    : categoryFilter
      ? titleCaseFromSlug(categoryFilter)
      : null;
  const formatScopeDate = (value: string) => new Date(value).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  const intervalLabel = dateFrom && dateTo ? `${formatScopeDate(dateFrom)} – ${formatScopeDate(dateTo)}` : 'All dates';

  const toggleBulkSelection = (row: CanonicalTransactionRow, checked: boolean) => {
    const lineItem = row.line_items[0];
    if (!lineItem) return;
    setSelectedLineItems((current) => {
      const next = new Map(current);
      if (checked) {
        next.set(lineItem.line_item_id, {
          expectedVersion: lineItem.version,
          canonicalTransactionId: row.canonical_transaction_id,
        });
      } else {
        next.delete(lineItem.line_item_id);
      }
      return next;
    });
    setBulkError(null);
    setBulkPhase((current) => current === 'error' ? 'idle' : current);
  };

  const buildBulkEdits = (): AnnotationEdit[] | null => {
    const firstSelectedId = selectedLineItems.keys().next().value as string | undefined;
    const firstRow = firstSelectedId ? lineItemRows.get(firstSelectedId) : undefined;
    const firstItem = firstRow?.line_items[0];
    const taxonomyVersion = categoriesQuery.data?.taxonomy_version;
    const targetCategory = firstItem?.category_id
      ? categoriesQuery.data?.categories.find((category) => category.category_id === firstItem.category_id)
      : undefined;
    if (!firstItem?.category_id || !targetCategory || !taxonomyVersion) {
      setBulkError('The selected category is unavailable. Refresh and try again.');
      setBulkPhase('error');
      return null;
    }
    const changes = {
      category: { category_id: targetCategory.category_id, taxonomy_version: taxonomyVersion },
    } as const;
    return Array.from(selectedLineItems.entries()).map(([lineItemId, selection]) => ({
      line_item_id: lineItemId,
      expected_version: selection.expectedVersion,
      changes,
    }));
  };

  const previewBulkSelection = async () => {
    const edits = buildBulkEdits();
    if (!edits || edits.length === 0) return;
    setBulkError(null);
    setBulkPhase('previewing');
    setBulkEdits(edits);
    try {
      const result = await bulkPreview.mutateAsync({
        edits,
        scope_fingerprint: Array.from(selectedLineItems.entries())
          .map(([lineItemId, selection]) => `${lineItemId}:${selection.expectedVersion}`)
          .sort()
          .join('|'),
      });
      setBulkPreviewData(result);
      setBulkPhase('previewed');
    } catch {
      setBulkError('Couldn’t preview these categories. Try again.');
      setBulkPhase('error');
    }
  };

  const handleBulkResult = (result: PublicMutationResult) => {
    if (result.kind === 'conflict') {
      setBulkError(result.code === 'changed_since'
        ? 'Nothing was changed. One or more selected items changed since you loaded them. Refresh to review the latest categories.'
        : 'This bulk change could not be applied. Review the selected items and try again.');
      setBulkPhase('error');
      return;
    }
    if (result.kind === 'unavailable') {
      setBulkError('We couldn’t confirm the bulk change. Refresh to check the latest categories.');
      setBulkPhase('error');
      return;
    }
    if (result.data.applied_count > 0) {
      const patchedRoots = patchLineItemCaches(queryClient, result.data.line_items);
      for (const root of ['transactions', 'inbox', 'spend-by-category', 'spend-by-merchant']) {
        if (!patchedRoots.has(root)) void queryClient.invalidateQueries({ queryKey: [root] });
      }
      setLastBatchId(result.data.batch_id);
      setLastBatchCount(result.data.applied_count);
      setLastBatchUndone(false);
    }
    setSelectedLineItems(new Map());
    setBulkPreviewData(null);
    setBulkEdits([]);
    setBulkError(null);
    setBulkPhase('saved');
  };

  const commitBulkSelection = async () => {
    if (!bulkPreviewData || bulkEdits.length === 0 || bulkPhase !== 'previewed') return;
    setBulkError(null);
    setBulkPhase('committing');
    try {
      const result = await bulkPatch.mutateAsync({ edits: bulkEdits });
      handleBulkResult(result);
    } catch {
      setBulkError('We couldn’t confirm the bulk change. Refresh to check the latest categories.');
      setBulkPhase('error');
    }
  };

  const undoLastBulk = async () => {
    if (!lastBatchId || bulkPhase === 'undoing') return;
    setBulkError(null);
    setBulkPhase('undoing');
    try {
      const result = await batchUndo.undo.mutateAsync(lastBatchId);
      const undoResult = result as PublicUndoResult;
      if (undoResult.kind === 'success' || undoResult.kind === 'replay') {
        patchLineItemCaches(queryClient, undoResult.data.line_items);
        setLastBatchUndone(true);
        setBulkPhase('saved');
      } else if (undoResult.kind === 'conflict' && undoResult.code === 'changed_since') {
        setBulkError('This batch changed since it was applied. Refresh to see the latest categories; nothing was undone.');
        setBulkPhase('error');
      } else if (undoResult.kind === 'unavailable') {
        setBulkError('We couldn’t confirm the batch Undo. Refresh to check the latest categories.');
        setBulkPhase('error');
      } else {
        setBulkError('This batch is no longer available to Undo. Refresh to see the latest categories.');
        setBulkPhase('error');
      }
    } catch {
      setBulkError('We couldn’t confirm the batch Undo. Refresh to check the latest categories.');
      setBulkPhase('error');
    }
  };

  const confirmCandidate = async (candidateId: string) => {
    setPendingCandidateIds((current) => new Set(current).add(candidateId));
    setCandidateErrors((current) => {
      const next = { ...current };
      delete next[candidateId];
      return next;
    });
    try {
      await confirmLink.mutateAsync({ candidateId, kind: 'transfer' });
    } catch {
      setCandidateErrors((current) => ({ ...current, [candidateId]: 'The transfer could not be confirmed. Try again.' }));
    } finally {
      setPendingCandidateIds((current) => {
        const next = new Set(current);
        next.delete(candidateId);
        return next;
      });
    }
  };

  const hasDisconnectedHistory = canonicalAccounts.some((account) =>
    account.status_detail === 'connection_disconnected' && (!accountFilter || account.canonical_account_id === accountFilter),
  );

  return (
    <div>
      {/* Scope line (spec §2 item 2): real interval + account. */}
      <div className="mt-1 text-xs text-muted-foreground" data-testid="feed-scope-header">Selected interval: {intervalLabel} · {accountLabel ?? (accountFilter ? 'Selected account' : 'All accounts')}{transactions.temporal?.availability === 'confirmed' ? ' (saved timezone)' : ''}</div>
      {temporalMetadataAbsent && (
        <div className="mt-3 space-y-2">
          <HonestInline kind="api-unavailable" />
          <p className="text-sm text-muted-foreground" data-testid="temporal-scope-unavailable-copy">
            Rows are shown, but saved-timezone calendar grouping is unavailable.
          </p>
        </div>
      )}
      {hasDisconnectedHistory && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">Saved history from a disconnected bank connection. New transactions are not syncing for that connection.</p>}
      {notice && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">{notice}</p>}
      {!notice && candidatesQuery.isPending && <p role="status" className="mb-3 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">Transfer suggestions are loading…</p>}
      {candidatesQuery.isError && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">Transfer suggestions could not be loaded. Transactions are still shown.{' '}<button type="button" className="mt-2 min-h-11 rounded-md border border-input px-3 underline" onClick={() => candidatesQuery.refetch()} disabled={candidatesQuery.isFetching}>{candidatesQuery.isFetching ? 'Retrying…' : 'Retry transfer suggestions'}</button></p>}
      {(categoryFilterLabel || merchantLabel) && <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="category-filter-banner"><span>Filtered by {categoryFilterLabel ?? merchantLabel}</span><Link to={scopeHref(`/transactions${location.hash}`, location.search)} className="min-h-11 inline-flex items-center text-primary underline">Clear</Link></div>}

      {groups.length === 0 ? (
        transactions.hasNextPage || transactions.isFetchingNextPage || transactions.isFetchNextPageError ? (
          <div data-testid="feed-awaiting-filtered-pages">
            <FeedNextPage
              hasNextPage={transactions.hasNextPage}
              isFetchingNextPage={transactions.isFetchingNextPage}
              isFetchNextPageError={transactions.isFetchNextPageError}
              fetchNextPage={transactions.fetchNextPage}
            />
          </div>
        ) : <RouteRecoveryScreen kind="no-data-in-range" frame="inline" />
      ) : (
        <div data-testid="feed-groups">
          {dayKeys.map((dayKeyValue) => {
            const group = groupByKey.get(dayKeyValue);
            return (
              <div key={dayKeyValue} data-day-key={dayKeyValue}>
                {group && <SelectableFeedDayGroup label={group.label} rows={group.rows} selected={selectedLineItems} onToggle={toggleBulkSelection} />}
                {(candidateMap.get(dayKeyValue) ?? []).map((candidate) => <TransferReviewCard key={candidate.candidate_id} candidate={candidate} confirming={pendingCandidateIds.has(candidate.candidate_id)} error={candidateErrors[candidate.candidate_id]} onConfirm={confirmCandidate} />)}
              </div>
            );
          })}
        </div>
      )}

      {(selectedLineItems.size > 0 || lastBatchId !== null || bulkError !== null) && (
        <div className="sticky bottom-3 z-30 mt-3 flex flex-wrap items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground shadow-lg" role="region" aria-label="Bulk actions" data-testid="bulk-actions-bar">
          <span>{pluralize(selectedLineItems.size, 'selected')}</span>
          <span aria-hidden="true">·</span>
          <button type="button" className="min-h-11 rounded-md px-3 font-medium hover:bg-primary-foreground/10 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => void previewBulkSelection()} disabled={selectedLineItems.size === 0 || bulkPhase === 'previewing' || bulkPhase === 'committing' || bulkPhase === 'undoing'}>
            {bulkPhase === 'previewing' ? 'Previewing…' : 'Categorize'}
          </button>
          <span aria-hidden="true">·</span>
          <button type="button" className="min-h-11 rounded-md px-3 font-medium opacity-60" disabled aria-disabled="true">Apply to similar</button>
          <span aria-hidden="true">·</span>
      <button type="button" className="min-h-11 rounded-md px-3 font-medium hover:bg-primary-foreground/10 disabled:cursor-not-allowed disabled:opacity-60" onClick={() => void undoLastBulk()} disabled={lastBatchId === null || lastBatchUndone || bulkPhase === 'undoing'}>
            {bulkPhase === 'undoing' ? 'Undoing…' : 'Undo last batch'}
          </button>
          {selectedLineItems.size > 0 && (
            <button type="button" className="ms-auto min-h-11 rounded-md px-3 underline hover:bg-primary-foreground/10" onClick={() => { setSelectedLineItems(new Map()); setBulkError(null); setBulkPhase('idle'); }}>
              Clear selection
            </button>
          )}
          {bulkError && <p className="basis-full text-sm" role="alert" data-testid="bulk-error">{bulkError}</p>}
          {bulkPhase === 'saved' && lastBatchId && <p className="basis-full text-sm" role="status" data-testid="bulk-saved-status">
            {lastBatchUndone
              ? `Restored · ${pluralize(lastBatchCount, 'item')} returned to its prior state. Feed and related views now agree.`
              : `Saved · ${pluralize(lastBatchCount, 'item')} updated. Feed and related views now agree.`}
          </p>}
          {bulkPreviewData && bulkPhase === 'previewed' && (
            <div className="basis-full rounded-md border border-primary-foreground/30 p-3" role="dialog" aria-label="Bulk categorization preview">
              <p>{pluralize(bulkPreviewData.eligible_count, 'eligible item')} · {pluralize(bulkPreviewData.unchanged_count, 'unchanged item')}</p>
              <p className="mt-1 text-xs text-primary-foreground/80">Counts are a preview; matches are checked again when applied.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className="min-h-11 rounded-md bg-primary-foreground px-3 font-medium text-primary" onClick={() => void commitBulkSelection()}>Apply {pluralize(bulkPreviewData.eligible_count, 'item')}</button>
                <button type="button" className="min-h-11 rounded-md border border-primary-foreground/50 px-3" onClick={() => { setBulkPreviewData(null); setBulkEdits([]); setBulkPhase('idle'); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {groups.length > 0 && (
        <FeedNextPage
          hasNextPage={transactions.hasNextPage}
          isFetchingNextPage={transactions.isFetchingNextPage}
          isFetchNextPageError={transactions.isFetchNextPageError}
          fetchNextPage={transactions.fetchNextPage}
        />
      )}
    </div>
  );
}

export function TransactionsPage() {
  const { canonicalAccounts, dateScopeAvailability, scopeAvailability } = useShellContext();
  const location = useLocation();
  // "View progress" (from the tools menu's running-job item, and from the
  // status row's own button) focuses the always-visible JobStatusRow below
  // rather than opening a second progress surface -- §5 keeps a single
  // running-job status source of truth.
  const jobStatusRowRef = useRef<HTMLDivElement>(null);
  const handleViewProgress = () => {
    jobStatusRowRef.current?.focus();
    jobStatusRowRef.current?.scrollIntoView({ block: 'nearest' });
  };
  const normalizedAccountIds = [...new Set(new URLSearchParams(location.search).getAll('account').map((value) => value.trim()).filter(Boolean))];
  const hasConflictingAccountFilters = normalizedAccountIds.length > 1;
  const selectedAccountId = normalizedAccountIds.length === 1 ? normalizedAccountIds[0] : null;
  const selectedCanonicalAccounts = selectedAccountId ? canonicalAccounts?.filter((account) =>
    account.canonical_account_id === selectedAccountId ||
    (account.canonical_account_id === null && account.account_id === selectedAccountId),
  ) ?? [] : [];
  const selectedCanonicalAccount = selectedCanonicalAccounts.length === 1 ? selectedCanonicalAccounts[0] : undefined;
  const reviewEligibility: ReviewLaunchEligibility | null = scopeAvailability === 'ready' && canonicalAccounts !== undefined
    ? hasConflictingAccountFilters || selectedCanonicalAccounts.length > 1
      ? { kind: 'ambiguous' }
      : selectedCanonicalAccount ? classifyReviewLaunch(selectedCanonicalAccount) : { kind: 'no_target' }
    : null;

  const reviewMessage = reviewEligibility?.kind === 'no_target' && selectedCanonicalAccount
    ? 'Account review is not available for this account.'
    : reviewEligibility?.kind === 'ambiguous'
      ? 'Account review needs a single active connection.'
      : reviewEligibility?.kind === 'setup'
        ? 'Account review needs setup.'
        : null;

  return (
    <section aria-labelledby="activity-heading" className="mx-auto w-full max-w-5xl p-4">
      {reviewEligibility?.kind === 'eligible' ? (
        <AccountReviewHost
          key={`${reviewEligibility.launch.canonicalAccountId}:${reviewEligibility.launch.replacementItemId}`}
          launch={reviewEligibility.launch}
          location={location}
          onViewProgress={handleViewProgress}
        />
      ) : (
        <header className="flex items-center justify-between gap-3">
          <h1 id="activity-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
          {/* Title row (spec §2 item 1): "Transactions" at left, one
              "Categorization tools" entry at right -- 44px height. The prior
              duplicate page-level "Review" link and the always-visible
              Reclassify button + rule composer are gone: bulk tools now live
              behind this one on-demand entry, alongside the account-review
              Review link. */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {reviewMessage && <span className="text-sm text-muted-foreground">{reviewMessage}</span>}
            <CategorizationTools onViewProgress={handleViewProgress} />
            <Link to={scopeHref(`/transactions/inbox${location.hash}`, location.search)} className="min-h-11 inline-flex items-center text-sm text-primary underline">Review</Link>
          </div>
        </header>
      )}
      {/* Local navigation (spec §2 item 3): text links + underline, reused ViewSwitcher. */}
      <ViewSwitcher />
      {/* Status slot (spec §2 item 4): compact job status, only when a job
          is known. Focusable so "View progress" from the tools menu can
          move focus/scroll here instead of opening a second surface. */}
      <div ref={jobStatusRowRef} tabIndex={-1}>
        <JobStatusRow onViewProgress={handleViewProgress} />
      </div>
      <DateScopeGuard availability={dateScopeAvailability}><TransactionsPageContent /></DateScopeGuard>
    </section>
  );
}

export default TransactionsPage;
